import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import ButtonPrimary from "../buttons/primary/primary";
import ButtonSecondary from "../buttons/secondary/secondary";

// Definindo tipos para TypeScript
interface CompletedSteps {
  [k: number]: boolean;
}

interface ButtonLabels {
  back: string;
  cancel: string;
  next: string;
  complete: string;
  reset: string;
  finish: string;
}

interface CustomStepperProps {
  steps: string[];
  renderStepContent: (step: number) => React.ReactNode;
  isStepValid: (step: number) => boolean;
  onComplete: () => void;
  onCancel: () => void;
  onReset: () => void;
  buttonLabels?: ButtonLabels;
}

const CustomStepper = ({
  steps,
  renderStepContent,
  isStepValid,
  onComplete,
  onCancel,
  onReset,
  buttonLabels = {
    back: "Voltar",
    cancel: "Cancelar",
    next: "Concluir Etapa",
    complete: "Finalizar",
    reset: "Reiniciar",
    finish: "Concluir"
  }
}: CustomStepperProps) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [completed, setCompleted] = useState<CompletedSteps>({});

  const totalSteps = (): number => steps.length;
  const completedSteps = (): number => Object.keys(completed).length;
  const isLastStep = (): boolean => activeStep === totalSteps() - 1;
  const allStepsCompleted = (): boolean => completedSteps() === totalSteps();
  
  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleNext = (): void => {
    if (activeStep === steps.length - 1) {
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = (): void => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = (): void => {
    const newCompleted: CompletedSteps = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    
    if (activeStep < steps.length - 1) {
      handleNext();
    } else if (allStepsCompleted()) {
      onComplete && onComplete();
    }
  };

  const handleReset = (): void => {
    setActiveStep(0);
    setCompleted({});
    onReset && onReset();
  };

  const isLastStepComplete = (): boolean => {
    return isLastStep() && completed[steps.length - 1];
  };

  return (
    <>
      {/* Stepper */}
      <Stepper nonLinear activeStep={activeStep} sx={{ 
        mb: 4,
        '& .MuiStepLabel-root .Mui-active': {
          color: '#4aaa9c', 
        },
        '& .MuiStepLabel-root .Mui-completed': {
          color: '#4aaa9c', 
        },
      }}>
        {steps.map((label, index) => (
          <Step key={label} completed={completed[index]}>
            <StepButton color="inherit" onClick={handleStep(index)}>
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      
      {/* Conteúdo do passo atual */}
      {renderStepContent(activeStep)}
      
      {/* Botões de navegação */}
      <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', pt: 3, gap: '10px', justifyContent: 'space-between', mt: 3 }}>
        {/* Área para botões da esquerda */}
        <Box sx={{ display: 'flex', gap: '10px' }}>
          {/* Botão Voltar */}
          {activeStep !== 0 && (
            <ButtonPrimary
              title={buttonLabels.back}
              onClick={handleBack}
              disabled={activeStep === 0}
            />
          )}
          
          {/* Botão Cancelar */}
          <ButtonSecondary 
            title={buttonLabels.cancel}
            onClick={onCancel}
          />
        </Box>
        
        {/* Espaçador */}
        <Box sx={{ flex: '1 1 auto' }} />
        
        {/* Área para botões da direita */}
        <Box sx={{ display: 'flex', gap: '10px' }}>
          {isLastStepComplete() ? (
            <>
              <ButtonPrimary
                title={buttonLabels.finish}
                onClick={onComplete}
              />
              <ButtonPrimary
                title={buttonLabels.reset}
                onClick={handleReset}
              />
            </>
          ) : (
            <>
              <ButtonPrimary
                title={isLastStep() ? buttonLabels.complete : buttonLabels.next}
                onClick={handleComplete}
                disabled={!isStepValid(activeStep)}
              />
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CustomStepper;